/**
 * Communication API Configuration
 * Handles environment-specific API settings with validation and health checks
 * Ensures robust API connectivity for both development and production
 */

import { config } from '@/config';

/**
 * Communication API endpoints configuration
 */
export const communicationApiConfig = {
  // Base API configuration - use main config
  baseUrl: config.apiUrl,
  
  // Communication-specific endpoints (aligned with backend routes)
  endpoints: {
    chatbot: {
      config: '/chatbot/config',
      message: '/chatbot/message',
      history: '/chatbot/history',
      analytics: '/chatbot/analytics',
      widget: (businessId: string) => `/api/chatbot/${businessId}/widget.js`,
      test: '/chatbot-test.html', // Frontend test page
    },
  },
  
  // Request configuration
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
  
  // Environment detection
  isDevelopment: import.meta.env.MODE === 'development',
  isProduction: import.meta.env.MODE === 'production',
  
  // Feature flags
  features: {
    enableDebugLogging: import.meta.env.MODE === 'development',
    enableRetry: true,
    enableHealthCheck: true,
  },
} as const;

/**
 * Get the backend URL for widget scripts with proper fallback and logging
 * This ensures the widget script loads from the correct backend domain
 */
export function getBackendUrlForWidget(): string {
  // Start with environment variable (most reliable)
  let backendUrl = import.meta.env.VITE_API_URL;
  
  // Fallback to main config
  if (!backendUrl) {
    backendUrl = config.apiUrl;
  }
  
  // Final fallback for production
  if (!backendUrl && communicationApiConfig.isProduction) {
    backendUrl = 'https://wildy-backend-production.up.railway.app/api';
  }
  
  // Development fallback
  if (!backendUrl && communicationApiConfig.isDevelopment) {
    backendUrl = 'http://localhost:3000/api';
  }
  
  // SECURITY: Force HTTPS in production to prevent Mixed Content errors
  if (communicationApiConfig.isProduction && backendUrl && backendUrl.startsWith('http://')) {
    backendUrl = backendUrl.replace('http://', 'https://');
    console.warn('[SECURITY] Forced HTTPS for production backend URL:', backendUrl);
  }
  
  // Final fallback - use HTTPS in production
  const finalUrl = backendUrl || (communicationApiConfig.isProduction 
    ? 'https://wildy-backend-production.up.railway.app/api' 
    : 'http://localhost:3000/api');
  
  // Production logging for debugging
  if (communicationApiConfig.isProduction) {
    console.log('[WIDGET URL DEBUG] Backend URL determination:', {
      fromViteEnv: import.meta.env.VITE_API_URL,
      fromConfig: config.apiUrl,
      finalUrl: finalUrl,
      httpsForced: finalUrl !== backendUrl,
      environment: import.meta.env.MODE,
      timestamp: new Date().toISOString()
    });
  }
  
  // Development logging
  if (communicationApiConfig.features.enableDebugLogging) {
    console.log('[DEV] Widget backend URL:', {
      source: backendUrl === import.meta.env.VITE_API_URL ? 'VITE_API_URL' : 
              backendUrl === config.apiUrl ? 'config.apiUrl' : 'fallback',
      url: finalUrl,
      environment: import.meta.env.MODE
    });
  }
  
  return finalUrl;
}

/**
 * Get the complete widget script URL for a business
 */
export function getWidgetScriptUrl(businessId: string): string {
  const backendUrl = getBackendUrlForWidget();
  const widgetPath = communicationApiConfig.endpoints.chatbot.widget(businessId);
  
  // Remove /api from the end of backendUrl if it exists, since widgetPath includes /api
  const cleanBackendUrl = backendUrl.replace(/\/api$/, '');
  const fullUrl = `${cleanBackendUrl}${widgetPath}`;
  
  // Production logging for debugging
  if (communicationApiConfig.isProduction) {
    console.log('[WIDGET URL DEBUG] Widget script URL generation:', {
      businessId,
      backendUrl,
      widgetPath,
      cleanBackendUrl,
      fullUrl,
      timestamp: new Date().toISOString()
    });
  }
  
  return fullUrl;
}

/**
 * Generate standardized embed code for website integration
 * Replaces inconsistent window.location.origin usage
 */
export function generateStandardEmbedCode(config: {
  businessId: string;
  websiteUrl: string;
  botName: string;
  welcomeMessage: string;
  primaryColor: string;
  position: string;
  autoOpen: boolean;
  autoOpenDelay: number;
  collectUserInfo: boolean;
  requireEmail: boolean;
}): string {
  const scriptUrl = getWidgetScriptUrl(config.businessId);
  
  return `<!-- Wildlife Chatbot Widget -->
<script>
  window.WildlifeChatbot = {
    businessId: "${config.businessId}",
    websiteUrl: "${config.websiteUrl}",
    botName: "${config.botName}",
    welcomeMessage: "${config.welcomeMessage}",
    primaryColor: "${config.primaryColor}",
    position: "${config.position}",
    autoOpen: ${config.autoOpen},
    autoOpenDelay: ${config.autoOpenDelay},
    collectUserInfo: ${config.collectUserInfo},
    requireEmail: ${config.requireEmail}
  };
</script>
<script src="${scriptUrl}"></script>`;
}

/**
 * Get the iframe embed URL for a business
 */
export function getIframeEmbedUrl(businessId: string): string {
  const backendUrl = getBackendUrlForWidget();
  // Remove /api from the end if it exists, since we'll add it manually
  const cleanBackendUrl = backendUrl.replace(/\/api$/, '');
  return `${cleanBackendUrl}/api/chatbot/${businessId}/embed`;
}

/**
 * Generate iframe embed code for CMS platforms (Google Sites, WordPress, etc.)
 */
export function generateIframeEmbedCode(businessId: string, options?: {
  width?: string;
  height?: string;
  frameborder?: string;
}): string {
  const embedUrl = getIframeEmbedUrl(businessId);
  const width = options?.width || '400';
  const height = options?.height || '600';
  const frameborder = options?.frameborder || '0';
  
  return `<iframe src="${embedUrl}" width="${width}" height="${height}" frameborder="${frameborder}"></iframe>`;
}

/**
 * Validate API configuration
 * Ensures all required environment variables are set
 */
export function validateApiConfig(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check base URL
  if (!communicationApiConfig.baseUrl) {
    errors.push('API base URL is not configured');
  }
  
  // Check if URL is valid
  try {
    new URL(communicationApiConfig.baseUrl);
  } catch {
    errors.push('API base URL is not a valid URL');
  }
  
  // Development-specific checks
  if (communicationApiConfig.isDevelopment) {
    if (!communicationApiConfig.baseUrl.includes('localhost')) {
      console.warn('Development mode detected but not using localhost');
    }
  }
  
  // Production-specific checks
  if (communicationApiConfig.isProduction) {
    if (!import.meta.env.VITE_API_URL) {
      errors.push('VITE_API_URL environment variable is required in production');
    }
    
    if (communicationApiConfig.baseUrl.includes('localhost')) {
      errors.push('Production should not use localhost URLs');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get full API endpoint URL
 */
export function getApiEndpoint(endpoint: string): string {
  const baseUrl = communicationApiConfig.baseUrl.replace(/\/$/, ''); // Remove trailing slash
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
}

/**
 * Get test page URL for chatbot testing
 */
export function getTestPageUrl(businessId: string): string {
  const baseUrl = window.location.origin; // Always use frontend origin for test page
  const testUrl = new URL(communicationApiConfig.endpoints.chatbot.test, baseUrl);
  testUrl.searchParams.set('businessId', businessId);
  return testUrl.toString();
}

/**
 * API health check
 * Validates that the API is accessible and responding
 */
export async function checkApiHealth(): Promise<{ healthy: boolean; error?: string }> {
  if (!communicationApiConfig.features.enableHealthCheck) {
    return { healthy: true };
  }
  
  try {
    const healthEndpoint = getApiEndpoint('/health');
    const response = await fetch(healthEndpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    if (response.ok) {
      return { healthy: true };
    } else {
      return { 
        healthy: false, 
        error: `API health check failed with status: ${response.status}` 
      };
    }
  } catch (error) {
    return { 
      healthy: false, 
      error: error instanceof Error ? error.message : 'Unknown health check error' 
    };
  }
}

/**
 * Log API configuration for debugging
 * Only logs in development mode
 */
export function logApiConfig(): void {
  if (!communicationApiConfig.features.enableDebugLogging) {
    return;
  }
  
  console.group('Communication API Configuration');
  console.log('Environment:', import.meta.env.MODE);
  console.log('Base URL:', communicationApiConfig.baseUrl);
  console.log('Endpoints:', communicationApiConfig.endpoints);
  console.log('Features:', communicationApiConfig.features);
  
  const validation = validateApiConfig();
  if (validation.isValid) {
    console.log('✅ Configuration is valid');
  } else {
    console.warn('⚠️ Configuration issues:', validation.errors);
  }
  
  console.groupEnd();
}

/**
 * Initialize API configuration
 * Call this when the app starts to validate and log configuration
 */
export function initializeCommunicationApi(): void {
  logApiConfig();
  
  const validation = validateApiConfig();
  if (!validation.isValid) {
    console.error('Communication API configuration errors:', validation.errors);
    
    // In development, show a more helpful error
    if (communicationApiConfig.isDevelopment) {
      console.error(`
🚨 Communication API Configuration Issues:
${validation.errors.map(error => `• ${error}`).join('\n')}

Please check your environment variables and configuration.
      `);
    }
  }
} 